/**
 * "Which PLANE does this path entity's stored geometry live in, and how does a
 * sample of it come back out in WCS?" — the ONE frame answer `./pathSampling.ts`
 * reads.
 *
 * ## Why this is a leaf of its own
 *
 * Split out of `./pathSampling.ts` when it was written (@codex PR #785
 * round 15, P2): that module answers "walk this curve by arc length", which is
 * a different question from "what frame is the answer in", and the OCS reading
 * carries enough rationale to push its host past the growth threshold
 * CLAUDE.md's file-size section sets. Same shape as `./heavyPolylineDrawnChain.ts`
 * and `./heavyPolylineCurveRefusal.ts`, the package's other single-truth leaves.
 *
 * The `PathEntity` / `PathSample` vocabulary these signatures are stated in
 * comes from `./pathSampleTypes.ts`, NOT from `./pathSampling.ts`: this leaf is
 * a dependency of the sampler, so taking even a type-only edge back to it
 * closes a cycle in the module graph that `.dependency-cruiser.cjs`'s
 * `no-circular` rule rejects outright (it has no type-only exception).
 *
 * Nothing here re-derives the Arbitrary Axis Algorithm. `computeOcsAxes` /
 * `ocsToWcs` / `isDefaultExtrusion` (`@dxf-ai-editor/drawing-model`) are the
 * repo's one implementation of it — the same one `wcsBoundsLeaf.ts`'s
 * `circleLocal` / `arcLocal` / `lwpolylineLocal` / `heavyPolylineLocal` and
 * `cad-core`'s `heavyPolylineWcsResolver` lift through — and this module only
 * says WHICH entity field feeds which slot of it.
 */

import {
  computeOcsAxes,
  isDefaultExtrusion,
  ocsToWcs,
  polylineSubtype,
  type OcsAxes,
} from "@dxf-ai-editor/drawing-model";

import type { PathEntity, PathSample } from "./pathSampleTypes";

/** The OCS plane a path entity's stored coordinates live in. */
export interface PathOcsPlane {
  /** The Arbitrary Axis Algorithm basis of the entity's group-210 normal. */
  readonly axes: OcsAxes;
  /** The plane's offset along that normal — the entity's own elevation. */
  readonly elevation: number;
}

/**
 * The plane `entity`'s stored coordinates are OBJECT coordinates in, or `null`
 * when they are already WCS and nothing has to be lifted.
 *
 * ## The defect this exists to close
 *
 * @codex PR #785 round 15, P2 ("Lift heavy ARRAYPATH samples from OCS into
 * WCS"). A CIRCLE / ARC / LWPOLYLINE / 2D heavy POLYLINE stores its plan
 * coordinates in the plane of its group-210 extrusion, at its own elevation
 * along that normal (Autodesk DXF reference, "OCS and Extrusion Direction").
 * Every reader of those entities in this repo already knows that —
 * `wcsBoundsLeaf.ts`'s `circleLocal` / `arcLocal` / `lwpolylineLocal` /
 * `heavyPolylineLocal`, `cad-core`'s `heavyPolylineWcsResolver`, the render
 * adapter, the picker and the plot all lift through the SAME
 * {@link computeOcsAxes} / {@link ocsToWcs} truth this function calls.
 *
 * The sampler did not, and `arrayReducers.ts` — its only consumer — places
 * clones by `sample.point` against entities that ARE in WCS. With extrusion
 * `(0, 0, -1)` the algorithm gives `Ax = (-1, 0, 0)`, so an OCS run from
 * `(0,0)` to `(10,0)` is DRAWN toward WCS `(-10, 0)` while ARRAYPATH marched
 * its copies toward `(+10, 0)`.
 *
 * ## Why the walk stays in the plane and only the SAMPLE is lifted
 *
 * Lifting the chain's vertices first would be the same answer for more work
 * and less accuracy. The OCS basis is ORTHONORMAL, so the lift is an ISOMETRY
 * of the plane into space: it preserves every chord and every arc length, and
 * therefore commutes with an arc-length walk — `lift(sample(d))` is
 * `sample(lift(...))(d)` exactly. Sampling in the plane additionally keeps the
 * group-42 bulges in the frame they are defined in (a bulge is a signed
 * tangent of a quarter of the included angle, whose SIDE is stated inside the
 * OCS plane), so no handedness bookkeeping is needed for a mirrored normal:
 * the reflection is carried by the basis, once, at the end. A "lift the
 * vertices, then sample in WCS" arm would instead have to negate bulges for an
 * orientation-reversing plane AND would foreshorten every arc length the
 * moment the plane is tilted out of XY.
 *
 * ## What is NOT fixed here, and what DECLINES it instead
 *
 * `PathSample.point` is `{x, y}` and `arrayReducers.ts` translates its clones
 * with a 2D `translatePoint`, so for a plane TILTED out of WCS XY the lifted
 * sample's Z is dropped and the copies would follow the curve's plan shadow —
 * foreshortened where the plane leans, collapsed onto a single point where it
 * stands on edge (extrusion `(0, 1, 0)` puts the OCS Y axis along WCS Z, so an
 * OCS path along it has no plan extent at all). That is the SAME
 * 2D-end-to-end limitation `./heavyPolylineCurveRefusal.ts` documents for its
 * `"polyline-3d"` refusal, and it is now answered the same way: @codex PR #785
 * round 16, P2, ARRAYPATH REFUSES such a path through
 * `./arrayPathPlaneRefusal.ts` rather than placing copies along the shadow.
 * That leaf holds the completion plan for both (widen the editor's one
 * `TransformFn` to move an entity in Z, then give `PathSample` a Z channel,
 * `pathTotalLength` a 3D chord sum and `arrayReducers` a three-axis translate).
 *
 * This function still returns the FULL lift for every plane, tilted included.
 * Deciding which planes a COMMAND may act on is not the frame reader's job, and
 * `samplePathAtDistance` is exported and must keep answering for whatever it is
 * handed — the same division of labour `./heavyPolylineCurveRefusal.ts` and the
 * sampler already keep.
 *
 * Two kinds are deliberately absent. A LINE: its group 210 does not move its
 * WCS endpoints, so there is nothing to lift (the same rule
 * `explodeHeavyPolyline.ts` states when it carries an extrusion onto a LINE).
 * And a heavy polyline that is NOT the 2D subtype: a 3D polyline and both
 * meshes store real WCS per-vertex data, so lifting them would MOVE the
 * entity — see the `case "Polyline"` arm below.
 * A default / absent extrusion returns `null` so the overwhelmingly common
 * planar drawing keeps its existing numbers BIT-EXACTLY, signed zero included
 * — the rule `ocsPlanarTransform` and `heavyPolylineWcsResolver` both follow.
 */
export function pathOcsPlane(entity: PathEntity): PathOcsPlane | null {
  switch (entity.type) {
    case "Line":
      return null;
    case "Circle":
    case "Arc":
      return isDefaultExtrusion(entity.extrusion)
        ? null
        : {
            axes: computeOcsAxes(entity.extrusion),
            // A CIRCLE / ARC's group 30 IS the plane offset along its own
            // normal — `wcsBoundsLeaf.ts::circleLocal` reads it the same way.
            elevation: entity.center.z ?? 0,
          };
    case "LWPOLYLINE":
      return isDefaultExtrusion(entity.extrusion)
        ? null
        : { axes: computeOcsAxes(entity.extrusion), elevation: entity.elevation };
    case "Polyline":
      // ONLY the 2D subtype is planar. A 3D polyline / polygon mesh /
      // poly-face mesh stores REAL WCS per-vertex data, its elevation means
      // nothing, and applying an OCS tilt to already-WCS vertices would MOVE
      // the entity — `heavyPolylineWcsResolver`'s own rule (it reports
      // `extrusion: undefined` for exactly these), followed here rather than
      // restated, so the two lifts of one entity can never disagree. The
      // curve commands refuse these subtypes long before sampling, but
      // `samplePathAtDistance` is exported and still answers for them with the
      // entity's own first recorded point — which must come back untouched.
      return polylineSubtype(entity) !== "2d" ||
        isDefaultExtrusion(entity.extrusion)
        ? null
        : {
            // Group 10/20/30's "dummy point": X/Y are 0 and Z is the
            // elevation. A 2D polyline's per-VERTEX group 30 is an OCS
            // duplicate of it and never an independent coordinate —
            // `heavyPolylineWcsResolver`'s own rule, followed here so the two
            // lifts of the same entity can never disagree.
            axes: computeOcsAxes(entity.extrusion),
            elevation: entity.elevation.z ?? 0,
          };
  }
}

/**
 * One in-plane sample re-expressed in WCS: the point through the plane's basis
 * at the entity's elevation, and the tangent as the IMAGE of its own direction
 * vector (`ocsToWcs` is purely linear, so a direction maps with `z = 0` and no
 * translation term). A mirrored normal therefore reverses the tangent, which
 * is what ARRAYPATH's `alignItems` rotates each copy by.
 *
 * When the lifted tangent is perpendicular to WCS XY — a plane standing on
 * edge, whose curve climbs straight up in plan — its shadow has no direction
 * and `Math.atan2(0, 0)` reports 0. That degenerate angle is no longer anything
 * a user can reach through ARRAYPATH: a tangent can only leave WCS XY on a
 * plane that is tilted out of it, and `./arrayPathPlaneRefusal.ts` declines
 * exactly those planes before the command samples (@codex PR #785 round 16,
 * P2). It is stated here rather than guarded here because this function is the
 * frame reader, not the command's gate, and the two-place split is what keeps
 * the refusal a single truth the reducer and its report both read.
 */
export function liftSampleToWcs(sample: PathSample, plane: PathOcsPlane): PathSample {
  // The two angle conversions are FUNCTION-LOCAL, not module constants, and
  // that is a testability decision rather than a style one: a module-level
  // `const` is evaluated once per worker at IMPORT time, so a mutation of it
  // is a STATIC mutant the Stryker vitest runner cannot activate — the module
  // is already loaded when the mutant switch is set, and the gate reports a
  // false "Survived" for an expression three tests here actually pin (the
  // CIRCLE tangent case in `./pathSampling.test.ts` and M5 / M6 in
  // `./pathSampling.heavyPolyline.metamorphic.test.ts` all fail when either
  // factor is wrong). Evaluating them per call costs two divisions and makes
  // the mutants dynamic, hence killable. `./pathSampling.ts`,
  // `./descriptors/simpleGeometry.ts` and `drawing-model/
  // hatchBoundaryGeometry.ts` each keep their own copy of these for the same
  // "a radian-degree factor is a mathematical constant, not a domain truth
  // that could drift" reason; importing them from `./pathSampling.ts` would
  // additionally make this leaf depend on its own consumer.
  const RAD2DEG = 180 / Math.PI;
  const DEG2RAD = Math.PI / 180;
  const point = ocsToWcs(
    { x: sample.point.x, y: sample.point.y, z: plane.elevation },
    plane.axes,
  );
  const rad = sample.tangentAngle * DEG2RAD;
  const tangent = ocsToWcs(
    { x: Math.cos(rad), y: Math.sin(rad), z: 0 },
    plane.axes,
  );
  return {
    point: { x: point.x, y: point.y },
    tangentAngle: Math.atan2(tangent.y, tangent.x) * RAD2DEG,
  };
}
